/**
 * The transformer the engine will size when "Recommended Rating" is on.
 *
 * The rating stored in `transformerDets.rated` is whatever was last typed by
 * hand; with auto-sizing on the engine ignores it and sizes the unit itself at
 * run time (BESS_feasibility/PV_output/system_simulation.py). Reading that
 * stale number back on the Technical page presented it as the answer -- a
 * 333 kVA leftover on a 1 MW plant. This mirrors the engine's rule so the page
 * can show the figure the run will actually use, marked as an estimate:
 *
 *   PV plant            inverters x Paco / pf
 *   hybrid, shared POI  max(POI export limit, (inverters x Paco + battery P_max_d) / pf)
 *                       (the battery term only once a chain loss mode is on,
 *                        as in the engine)
 *   storage-only        battery P_max_d / pf
 *
 * pf is clamped to 0.80-1.00 like pv_core/transformer_sizing.py.
 */

const DEFAULT_PF = 0.95;

const finite = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function transformerPowerFactor(transformerDets) {
  const pf = finite(transformerDets?.powerFactor);
  if (!pf) return DEFAULT_PF;
  return Math.max(0.8, Math.min(1.0, pf));
}

/** Count of inverters in the design: one key per installed unit. */
export function inverterCount(systemDesign) {
  return Object.keys(systemDesign || {}).length;
}

/**
 * @param {object} cfg           project config
 * @param {object} opts
 * @param {number|null} opts.inverterPacoKw  nameplate AC of one inverter, kW
 * @param {number|null} opts.batteryPowerKw  discharge power of the battery
 *        option the transformer must carry (largest option, count applied), kW
 * @param {boolean} opts.hasPv    whether the plant has a PV asset
 * @returns {{kva:number|null, note:string}} kVA (null when it cannot be told
 *          yet) and a short sentence of what it was sized from.
 */
export function recommendedTransformerKva(cfg, { inverterPacoKw, batteryPowerKw, hasPv = true } = {}) {
  const dets = cfg?.transformerDets || {};
  const pf = transformerPowerFactor(dets);
  const battery = Math.max(0, finite(batteryPowerKw) ?? 0);

  if (!hasPv) {
    if (!battery) return { kva: null, note: "battery discharge power over the power factor" };
    return { kva: battery / pf, note: `battery discharge power ${Math.round(battery).toLocaleString("en-GB")} kW over pf ${pf.toFixed(2)}` };
  }

  const n = inverterCount(cfg?.systemDesign);
  const paco = finite(inverterPacoKw);
  if (!n || !paco) {
    return { kva: null, note: "inverter capacity over the power factor; known once the inverters are designed" };
  }
  const acKw = n * paco;
  const chainOn = String(dets.chainLossMode || "off") !== "off";
  const sharedPoi = String(dets.topology || "shared_poi") !== "per_asset";
  const batteryTerm = chainOn && sharedPoi ? battery : 0;
  const limit = finite(cfg?.grid?.exportLimitKw);
  const poiFloor = chainOn && limit && limit > 0 && limit < 1e5 ? limit : 0;
  const demand = (acKw + batteryTerm) / pf;
  const kva = Math.max(poiFloor, demand);
  const parts = [`${n} × ${Math.round(paco).toLocaleString("en-GB")} kW inverter AC`];
  if (batteryTerm) parts.push(`${Math.round(batteryTerm).toLocaleString("en-GB")} kW battery discharge`);
  let note = `${parts.join(" + ")} over pf ${pf.toFixed(2)}`;
  if (poiFloor > demand) note = `held at the ${Math.round(poiFloor).toLocaleString("en-GB")} kW export limit (above ${note})`;
  return { kva, note };
}

/** "1,074 kVA" — whole kVA, thousands grouped in the reader's locale. */
export function formatKva(kva) {
  const n = finite(kva);
  return n === null ? "—" : `${Math.round(n).toLocaleString("en-GB")} kVA`;
}
